<template>
  <div
    ref="containerRef"
    class="player-container"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
    @pointerleave="handleMouseLeave"
    @click="handleClick"
  >
    <div
      ref="playerContainerRef"
      v-if="isShowVideo"
      :id="playerId"
      class="player"
      :class="{ muted: !hasAudio }"
      :style="{ backgroundColor: backgroundColor }"
    ></div>

    <AppImage :src="poster" v-if="isShowPoster" :customStyle="{ background: backgroundColor }" />
    <Transition name="video-icon">
      <VideoIcon v-if="showVideoTag && !isMouseInside" />
    </Transition>
  </div>
</template>

<script setup lang="ts">
  import { computed, type PropType, ref, onUnmounted, watchEffect, onMounted, toRaw } from 'vue'
  import Player from 'xgplayer'
  import 'xgplayer/dist/index.min.css'
  import AppImage from '../AppImage.vue'
  import type { VideoItem } from '@/types/video'
  import VideoIcon from '../VideoIcon/index.vue'

  defineOptions({
    name: 'VideoPlayer'
  })

  type Size = 'small' | 'medium' | 'large'

  const props = defineProps({
    idPrefix: {
      type: String,
      default: ''
    },
    closeVideoClick: {
      type: Boolean,
      default: true
    },
    showVideoTag: {
      type: Boolean,
      default: true
    },
    autoPlay: {
      type: Boolean,
      default: false
    },
    isShowControls: {
      type: Boolean,
      default: true
    },
    isShowFullScreen: {
      type: Boolean,
      default: true
    },
    isPauseWhenLeave: {
      type: Boolean,
      default: true
    },
    itemData: {
      type: Object as PropType<VideoItem>,
      default: () => ({})
    },
    onReady: {
      type: Function,
      default: () => {}
    },
    size: {
      type: String as PropType<Size>,
      default: 'medium'
    },
    closeVideoDblclick: {
      //关闭双击全屏
      type: Boolean,
      default: true
    },
    backgroundColor: {
      type: String,
      default: '#222'
    },
    videoFillMode: {
      type: String as PropType<'fillWidth' | 'fillHeight' | 'fill' | 'contain' | 'auto'>,
      default: 'auto'
    },
    cssFullscreen: {
      type: Boolean,
      default: false
    },
    clickFullscreen: {
      //是否点击全屏
      type: Boolean,
      default: false
    },
    // 是否使用start插件,暂停时有按钮常驻
    isUseStartPlugin: {
      type: Boolean,
      default: false
    },
    // 鼠标进入是否自动播放
    mouseEnterPlay: {
      type: Boolean,
      default: true
    },
    disableDynamicBg: {
      type: Boolean,
      default: true
    },
    autoplayMuted: {
      type: Boolean,
      default: false
    }
  })

  const emit = defineEmits(['videoFocus', 'videoBlur'])

  const player = ref<Player | null>()
  const isShowVideo = ref(props.autoPlay)
  const playerContainerRef = ref<HTMLElement>()
  const containerRef = ref<HTMLElement>()
  const isCssFullscreen = ref(false)
  // 跟踪鼠标是否在容器内，用于解决快速划过时的播放bug
  const isMouseInside = ref(false)

  // 保存原始父元素和位置信息，用于退出全屏时恢复
  let originalParent: HTMLElement | null = null
  let originalNextSibling: Node | null = null

  // 默认展示封面图
  const defaultIsShowPoster = ref(true)
  // 实际情况是否展示封面图
  // 自动播放的情况不需要展示封面图
  // 后端接口中没有封面字段时，不展示封面
  const isShowPoster = computed(() => {
    return (
      defaultIsShowPoster.value &&
      !props.autoPlay &&
      (props.itemData.firstFrameResize200Url ||
        props.itemData.firstFrameResize512Url ||
        props.itemData.firstFrameUrl)
    )
  })

  const poster = computed(() => {
    if (props.size === 'small') {
      return props.itemData.firstFrameResize200Url
    } else if (props.size === 'medium') {
      return props.itemData.firstFrameResize512Url
    } else {
      return props.itemData.firstFrameUrl
    }
  })

  const playerId = computed(() => {
    if (props.idPrefix) {
      return `player-${props.idPrefix}-${props.itemData.itemId}`
    }
    return `player-${props.itemData.itemId}`
  })

  const hasAudio = computed(() => {
    return props.itemData.videoExtraInfo?.hasAudio
  })

  const configFullScreen = () => {
    if (!props.isShowFullScreen) {
      player.value?.addClass('hide-full-screen')
    } else {
      player.value?.removeClass('hide-full-screen')
    }
  }

  const initPlayer = () => {
    const volumeStr = localStorage.getItem('VIDEO_VOLUME') ?? '0.5'
    const localVolumeNumber = Number(volumeStr)

    player.value = new Player({
      id: playerId.value,
      url: props.itemData.url,
      width: '100%',
      height: '100%',
      progress: {
        isDraggingSeek: false
      },
      volume: {
        default: props.autoPlay ? localVolumeNumber : 0, // 默认音量
        showValueLabel: true, //是否显示音量值
        miniVolume: 0.03 //静音恢复的时候最小音量
      },
      inactive: 2000, //播放器focus状态自动消失延迟时长
      closeVideoClick: props.closeVideoClick, //关闭视频点击的播放和暂停动作
      leavePlayerTime: 0, //鼠标离开播放器立即隐藏控制栏
      loop: true,
      fullscreen: {
        useCssFullscreen: props.cssFullscreen
      },
      cssFullscreen: props.cssFullscreen,
      autoplay: props.autoPlay,
      autoplayMuted: props.autoplayMuted,
      closePauseVideoFocus: true, //暂停时不触发focus事件
      closeVideoDblclick: props.closeVideoDblclick, //禁止双击全屏
      playbackRate: false, // 禁用倍速
      videoFillMode: props.videoFillMode,
      ignores: props.isUseStartPlugin ? ['enter'] : ['start', 'enter'], //禁用的插件
      controls: {
        mode: 'bottom'
      },
      dynamicBg: {
        disable: props.disableDynamicBg //关闭背景高斯模糊
      },

      icons: {
        startPlay: getIcon('hitems-video-play'),
        startPause: getIcon('hitems-video-pause'),
        play: getIcon('hitems-video-play'),
        pause: getIcon('hitems-video-pause'),
        volumeMuted: getIcon('hitems-muted'),
        volumeSmall: getIcon('hitems-video-small-sound'),
        volumeLarge: getIcon('hitems-video-sound'),
        fullscreen: getIcon('hitems-video-full'),
        exitFullscreen: getIcon('hitems-shrink-screen'),
        cssFullscreen: getIcon('hitems-video-full'),
        exitCssFullscreen: getIcon('hitems-shrink-screen')
      },
      commonStyle: {
        progressColor: '#595A5B', // 进度条底色
        playedColor: '#fff', //进度条已播放的颜色
        volumeColor: '#fff', // 音量颜色
        sliderBtnStyle: {
          backgroundColor: 'rgba(255,255,255,0.5)',
          width: '10px',
          height: '10px'
        }
      }
    })
  }

  const showControls = (show: boolean) => {
    if (!player.value) return
    if (show) {
      player.value.removeClass('hide-controls')
    } else {
      player.value.addClass('hide-controls')
    }
  }

  const startPlay = async () => {
    if (!player.value) return
    try {
      await player.value.play()
    } catch (error) {
      // 如果播放失败，则不进行播放
    }
  }

  const pausePlay = () => {
    if (!player.value) return
    player.value.pause()
  }

  const addListener = () => {
    if (!player.value) return

    // 视频数据加载完
    player.value.on('loadeddata', () => {
      // 只有在鼠标还在容器内或者是自动播放模式时才播放
      // 防止快速划过时，鼠标已离开但视频仍然播放的bug
      if (isMouseInside.value || props.autoPlay) {
        startPlay()
      }
      showControls(props.isShowControls)
      // 有时即使加载完了，还是会黑一下，所以需要少加点延时
      setTimeout(() => {
        defaultIsShowPoster.value = false
      }, 200)
    })

    // 切换全屏
    player.value.on('fullscreen_change', (isFullscreen: boolean) => {
      // 更新 CSS 全屏状态并移动 DOM
      // 当开启 cssFullscreen 配置时，xgplayer 会使用 CSS 全屏
      if (props.cssFullscreen) {
        isCssFullscreen.value = isFullscreen

        if (isFullscreen) {
          // 进入全屏：将容器移动到 body
          moveContainerToBody()
        } else {
          // 退出全屏：将容器移回原位
          restoreContainerPosition()
        }
      }

      // 针对初始不显示播放控制栏，全屏时要求显示控制栏的逻辑处理
      if (isFullscreen) {
        showControls(true)
      } else {
        showControls(props.isShowControls)
      }
    })

    // 音量变化
    player.value?.on('volumechange', () => {
      const volume = player.value!.muted ? '0' : player.value!.volume.toString()
      localStorage.setItem('VIDEO_VOLUME', volume)
    })

    // 聚焦
    player.value?.on('focus', () => {
      showControls(props.isShowControls || isCssFullscreen.value)
      emit('videoFocus')
    })

    // 失焦
    player.value?.on('blur', () => {
      showControls(false)
      emit('videoBlur')
    })
  }

  const getIcon = (iconClass: string, iconStyle: string = 'font-size: 36px;') => {
    return `<span class='${iconClass}' style='${iconStyle}'></span>`
  }

  // 将容器移动到 body，解决父元素 transform 导致 fixed 定位失效的问题
  const moveContainerToBody = () => {
    if (!containerRef.value) return

    // 保存原始位置信息
    originalParent = containerRef.value.parentElement
    originalNextSibling = containerRef.value.nextSibling

    // 添加全屏样式类
    containerRef.value.classList.add('player-fullscreen-teleport')

    // 移动到 body
    document.body.appendChild(containerRef.value)
  }

  // 将容器恢复到原始位置
  const restoreContainerPosition = () => {
    if (!containerRef.value || !originalParent) return

    // 移除全屏样式类
    containerRef.value.classList.remove('player-fullscreen-teleport')

    // 恢复到原始位置
    if (originalNextSibling) {
      originalParent.insertBefore(containerRef.value, originalNextSibling)
    } else {
      originalParent.appendChild(containerRef.value)
    }
  }

  // 鼠标进入视频区域，如果没有video容器，先让容器显示
  // 如果player已经存在，则播放
  const handleMouseEnter = () => {
    isMouseInside.value = true
    isShowVideo.value = true
    if (player.value && props.mouseEnterPlay) {
      startPlay()
    }
  }

  const checkInSide = (e: MouseEvent) => {
    const rect = containerRef.value?.getBoundingClientRect()
    if (!rect) return false
    const isOutside =
      e.clientX < rect.left ||
      e.clientX > rect.right ||
      e.clientY < rect.top ||
      e.clientY > rect.bottom

    return !isOutside
  }

  const onMouseLeave = () => {
    isMouseInside.value = false
    player.value?.blur()
    if (player.value && props.isPauseWhenLeave) {
      player.value.pause()
    }
  }

  // 鼠标离开视频框时是否需要暂停视频
  const handleMouseLeave = (e: MouseEvent) => {
    // 因为卡片的进度条不在卡片范围内，会导致虽然鼠标在卡片内，但是触发了假的鼠标离开事件
    const isInSide = checkInSide(e)
    if (isInSide) {
      handleMouseEnter()
      return
    }
    onMouseLeave()
  }

  onMounted(() => {
    document.addEventListener('mousemove', handleMouseMove)
  })

  const handleMouseMove = (e: MouseEvent) => {
    if (isMouseInside.value) {
      const rect = containerRef.value?.getBoundingClientRect()
      if (!rect) return
      const isInSide = checkInSide(e)
      if (isInSide) {
        handleMouseEnter()
        return
      }
      onMouseLeave()
    }
  }

  // 销毁player
  onUnmounted(() => {
    // 如果还在全屏状态，先恢复位置
    if (isCssFullscreen.value) {
      restoreContainerPosition()
    }

    if (player.value) {
      player.value.destroy()
      player.value = null
    }

    // 解决在页面中快速移动的时候，有的时候鼠标离开事件没有触发
    // 监听全局的鼠标移动事件来解决此问题
    document.removeEventListener('mousemove', handleMouseMove)
  })

  // 当video容器显示后，开始初始换player
  watchEffect(() => {
    if (playerContainerRef.value && playerId.value) {
      initPlayer()
    }
  })

  // player初始化成功后，添加事件监听
  watchEffect(() => {
    if (player.value) {
      addListener()
      configFullScreen()
    }
  })

  const handleClick = () => {
    if (props.clickFullscreen && props.cssFullscreen && !isCssFullscreen.value) {
      actionCssFullscreen()
    }
  }

  const actionFullScreen = () => {
    player.value?.getFullscreen()
  }

  const actionCssFullscreen = () => {
    player.value?.getCssFullscreen()
  }

  defineExpose({
    startPlay,
    pausePlay,
    actionFullScreen,
    actionCssFullscreen
  })
</script>

<style scoped lang="stylus">
  @import './index.styl'
</style>
